import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [headerSettings, setHeaderSettings] = useState(null);
  const [signinImageUrl, setSigninImageUrl] = useState(null);
  const [signinLogoUrl, setSigninLogoUrl] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Password reset state
  const [resetMode, setResetMode] = useState(false); // false = login, true = reset
  const [resetStep, setResetStep] = useState(1); // 1=email, 2=question+answer+new pw
  const [resetEmail, setResetEmail] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/settings/header`);
        if (data && data.logo_filename) setHeaderSettings(data);
        if (data && data.signin_image_filename) {
          setSigninImageUrl(`${BACKEND_URL}/api/settings/signin-image?t=${Date.now()}`);
        }
        if (data && data.signin_logo_filename) {
          setSigninLogoUrl(`${BACKEND_URL}/api/settings/signin-logo?t=${Date.now()}`);
        }
      } catch {
        // Settings load is optional
      }
      setSettingsLoaded(true);
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleResetStep1 = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/get-security-question`, { email: resetEmail });
      setSecurityQuestion(data.security_question);
      setResetStep(2);
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Failed to find account');
    }
    setResetLoading(false);
  };

  const handleResetStep2 = async (e) => {
    e.preventDefault();
    setResetError('');
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setResetError('Password must be at least 6 characters');
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        email: resetEmail,
        security_answer: securityAnswer,
        new_password: newPassword
      });
      toast.success('Password reset successfully! Please sign in.');
      exitResetMode();
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Failed to reset password');
    }
    setResetLoading(false);
  };

  const exitResetMode = () => {
    setResetMode(false);
    setResetStep(1);
    setResetEmail('');
    setSecurityQuestion('');
    setSecurityAnswer('');
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-6 sm:mb-8">
            {(signinLogoUrl || headerSettings?.logo_filename) && (
              <div className="mb-4 sm:mb-6" data-testid="login-logo">
                <img
                  src={signinLogoUrl || `${BACKEND_URL}/api/settings/logo?t=${Date.now()}`}
                  alt="Logo"
                  className="object-contain"
                  style={{
                    height: Math.min(headerSettings?.logo_size || 80, 120),
                    maxWidth: '100%'
                  }}
                />
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              StudyPlan Pro
            </h1>
            <p className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>
              {resetMode ? 'Reset your password.' : 'Sign in to continue your study journey.'}
            </p>
          </div>

          {!resetMode ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                    {error}
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="w-full" data-testid="login-email-input" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-slate-700 font-medium mb-2 block">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="w-full" data-testid="login-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors" data-testid="login-submit-button">
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setResetMode(true)}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>

              <p className="mt-4 text-center text-slate-600 text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>
                Don't have an account?{' '}
                <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="register-link">
                  Sign up
                </Link>
              </p>
            </>
          ) : (
            <div className="space-y-5">
              {resetError && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" data-testid="reset-error">
                  {resetError}
                </div>
              )}

              {resetStep === 1 && (
                <form onSubmit={handleResetStep1} className="space-y-5">
                  <div>
                    <Label htmlFor="reset-email" className="text-slate-700 font-medium mb-2 block">Email Address</Label>
                    <Input id="reset-email" type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required placeholder="you@example.com" className="w-full" data-testid="reset-email-input" />
                  </div>
                  <Button type="submit" disabled={resetLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg" data-testid="reset-step1-submit">
                    {resetLoading ? 'Looking up...' : 'Continue'}
                  </Button>
                </form>
              )}

              {resetStep === 2 && (
                <form onSubmit={handleResetStep2} className="space-y-5">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-blue-800">{securityQuestion}</p>
                  </div>
                  <div>
                    <Label htmlFor="security-answer" className="text-slate-700 font-medium mb-2 block">Your Answer</Label>
                    <Input id="security-answer" type="text" value={securityAnswer} onChange={(e) => setSecurityAnswer(e.target.value)} required placeholder="Enter your answer" className="w-full" data-testid="reset-answer-input" />
                  </div>
                  <div>
                    <Label htmlFor="new-password" className="text-slate-700 font-medium mb-2 block">New Password</Label>
                    <Input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required placeholder="Min 6 characters" className="w-full" data-testid="reset-new-password-input" />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password" className="text-slate-700 font-medium mb-2 block">Confirm Password</Label>
                    <Input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required placeholder="Confirm new password" className="w-full" data-testid="reset-confirm-password-input" />
                  </div>
                  <Button type="submit" disabled={resetLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg" data-testid="reset-step2-submit">
                    {resetLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              )}

              <div className="text-center">
                <button onClick={exitResetMode} className="text-slate-600 hover:text-slate-800 text-sm font-medium" data-testid="back-to-login">
                  Back to Sign In
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={`hidden lg:flex flex-1 relative overflow-hidden transition-colors duration-300 ${imageReady ? 'bg-slate-900' : 'bg-white'}`}>
        {settingsLoaded && signinImageUrl && (
          <img
            src={signinImageUrl}
            alt="Sign-in background"
            className={`w-full h-full object-cover transition-opacity duration-300 ${imageReady ? 'opacity-80' : 'opacity-0'}`}
            onLoad={() => setImageReady(true)}
            data-testid="signin-background-image"
          />
        )}
        {imageReady && <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent"></div>}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-50 bg-amber-50 border-t border-amber-200 px-4 py-2 text-center text-xs sm:text-sm text-amber-800" data-testid="beta-disclaimer">
        BETA version in progress - If you encounter any technical issues or need help, please contact us at{' '}
        <a href="mailto:service@omnipear.com" className="font-semibold underline hover:text-amber-900">service@omnipear.com</a>
      </div>
    </div>
  );
};
